import api from "../../api/client";
import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { PDFDownloadLink, PDFViewer } from "@react-pdf/renderer";
import { Link } from "react-router-dom";
import { subMonths, startOfMonth, endOfMonth, format } from "date-fns";
import { useFarmProfile } from "../../contexts/FarmProfileContext.js";
import { formatFullDate } from "../../utils/dateUtils.js";
import { formatKES } from "../../utils/currency.js";
import Receipt from "./GeneralReceipt01.js";
import { PDFContent } from "../../components/Receipts/index.js";
import config from "../../config";

// Ant Design imports
import {
  Table,
  Select,
  DatePicker,
  Button,
  Modal,
  Form,
  Input,
  Row,
  Col,
  Card,
  Space,
  message,
  Tag,
  Statistic,
  Divider,
  InputNumber,
  ConfigProvider,
  Descriptions,
  Alert,
  Typography,
} from "antd";
import {
  SearchOutlined,
  FileExcelOutlined,
  FilePdfOutlined,
  PrinterOutlined,
  DownloadOutlined,
  DollarOutlined,
  RollbackOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ProTable from "@ant-design/pro-table";
import { fetchSuppliers, fetchUsers } from "../../api.js";
import { useSelector } from "react-redux";
import { useCallback } from "react";
import { groupAccountOptions } from "../../utils/account.js";
import enUS from "antd/lib/locale/en_US";
import SaleReversalModal from "../../components/SaleReversalModal.js";

const { RangePicker } = DatePicker;
const { Option } = Select;
const { Text } = Typography;

const SalesInvoices = () => {
  const [transactionData, setTransactionData] = useState([]);
  const [stake, setStakeholders] = useState([]);
  const [showReceipt, setShowReceipt] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const environment =
    window.location.hostname === "localhost" ? "development" : "production";
  const apiUrl = config[environment].apiUrl;
  const [receiptData, setReceiptData] = useState(null);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const account = useSelector((state) => state.account.user);
  const [confirmationData, setConfirmationData] = useState({
    id: null,
    description: null,
  });
  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);
  const { profile } = useFarmProfile();
  const [data, setData] = useState([]);
  const [metrics, setMetrics] = useState({});
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalRows, setTotalRows] = useState(0);
  const [filters, setFilters] = useState({
    period: "", // value string instead of object
    type: null,
    stakeholderId: null,
    paymentStatus: null,
    agingStatus: null,
    startDate: "",
    endDate: "",
  });
  // Payment modal state
  const [paymentModalVisible, setPaymentModalVisible] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);
  const [users, setUsers] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  // Inside the SalesInvoices component
  const [form] = Form.useForm(); // for payment modal
  const [paidAccounts, setPaidAccounts] = useState([]);
  const [selectedSale, setSelectedSale] = useState(null);
  const [reversalModalVisible, setReversalModalVisible] = useState(false);

  // Fetch paid-through accounts for the form (used in select options)
  const fetchPaidAccounts = useCallback(async () => {
    try {
      const { data } = await api.get(`/api/paid-through`);
      setPaidAccounts(data);
    } catch (error) {
      console.error("Failed to fetch paid-through accounts", error);
    }
  }, [apiUrl]);

  useEffect(() => {
    fetchPaidAccounts();
  }, [fetchPaidAccounts]);

  useEffect(() => {
    const loadFilters = async () => {
      try {
        const [supplierData, userData] = await Promise.all([
          fetchSuppliers(),
          fetchUsers(),
        ]);
        setSuppliers(supplierData);
        setUsers(userData);
      } catch (error) {
        console.error(error);
      }
    };
    loadFilters();
  }, []);

  const periodOptions = [
    { value: "today", label: "Today" },
    { value: "week", label: "This Week" },
    { value: "month", label: "This Month" },
    { value: "year", label: "This Year" },
    { value: "custom", label: "Custom Range" },
  ];

  const paymentOptions = [
    { value: "fully_paid", label: "Fully Paid" },
    { value: "partially_paid", label: "Partially Paid" },
    { value: "unpaid", label: "Unpaid" },
    { value: "cash", label: "Cash" },
  ];

  const agingOptions = [
    { value: "Not Due", label: "Not Due" },
    { value: "1-7 Days", label: "1-7 Days" },
    { value: "8-30 Days", label: "8-30 Days" },
    { value: "31-60 Days", label: "31-60 Days" },
    { value: "Over 60 Days", label: "Over 60 Days" },
    { value: "fully_paid", label: "Paid" },
  ];

  const stakeholderOptions = stake.map((stakeholder) => ({
    value: stakeholder.id,
    label: stakeholder.name,
  }));

  // Fetch report with server-side pagination & filters
  const fetchReport = async () => {
    setLoading(true);
    const query = new URLSearchParams({
      period: filters.period,
      type: filters.type || "",
      stakeholderId: filters.stakeholderId || "",
      paymentStatus: filters.paymentStatus || "",
      agingStatus: filters.agingStatus || "",
      startDate: filters.startDate,
      endDate: filters.endDate,
      page,
      limit: rowsPerPage,
    }).toString();

    try {
      const res = await api.get(`/all-sales-history?${query}`);
      const result = await res.data;
      setData(result.data || []);
      setMetrics(result.metrics || {});
      setTotalRows(result.total || 0);
    } catch (err) {
      message.error("Failed to fetch sales data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReport();
  }, [filters, page, rowsPerPage]);

  // Fetch receipt data when transactionId changes
  useEffect(() => {
    const fetchTransactionData = async () => {
      if (!transactionId) return;
      try {
        const response = await api.get(
          `/all-summary-receipts/${transactionId}`,
        );
        const { receiptData } = response.data;
        setReceiptData(receiptData[0]);
      } catch (error) {
        console.error("Error retrieving receipt data:", error);
      }
    };
    fetchTransactionData();
  }, [transactionId, apiUrl]);

  // Fetch stakeholders for dropdown
  useEffect(() => {
    const fetchSuppliers = async () => {
      try {
        const response = await api.get(`/stakeholders`);
        setStakeholders(response.data);
      } catch (error) {
        console.error("Error fetching suppliers:", error);
      }
    };
    fetchSuppliers();
  }, [apiUrl]);

  const handleCloseReceiptModal = () => {
    setShowReceiptModal(false);
    setReceiptData(null);
  };

  // CSV Export (manual)
  const csvLinkRef = useRef();

  const exportToCSV = () => {
    const csvData = [
      [
        "TXNID",
        "Customer",
        "Aging Days",
        "Invoice Date",
        "Due Date",
        "Generated By",
        "Farm",
        "Amount",
        "Balance",
        "Status",
        "Notes",
      ],
      ...data.map((item) => [
        item.transactionId,
        item.stakeholderId
          ? stake.find((s) => s.id === item.stakeholderId)?.name ||
            "Walk-in Customer"
          : "Walk-in Customer",
        item.agingDays,
        item.recorded || "N/A",
        item.dueDate || "N/A",
        item.servedBy,
        item.farm,
        item.amount,
        item.balance || 0,
        item.agingDays <= 0
          ? "Not Due"
          : item.paymentStatus === "fully_paid"
          ? "Fully Paid"
          : "Overdue",
        item.notes || "",
      ]),
    ];

    const csvContent = csvData.map((row) => row.join(",")).join("\n");
    const blob = new Blob(["\ufeff", csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const csvUrl = URL.createObjectURL(blob);
    csvLinkRef.current.href = csvUrl;
    csvLinkRef.current.download = "sfarms.csv";
    csvLinkRef.current.click();
  };

  // PDF Export using jsPDF
  const exportToPDF = () => {
    const doc = new jsPDF();
    doc.autoTable({
      head: [
        [
          "TXNID",
          "Customer",
          "Aging Days",
          "Invoice Date",
          "Due Date",
          "By",
          "Farm",
          "Amount",
          "Balance",
          "Status",
          "Notes",
        ],
      ],
      body: data.map((user) => [
        user.transactionId,
        user.stakeholderId
          ? stake.find((s) => s.id === user.stakeholderId)?.name ||
            "Walk-in Customer"
          : "Walk-in Customer",
        user.agingDays,
        user.recorded ? formatFullDate(user.recorded) : "N/A",
        user.dueDate ? formatFullDate(user.dueDate) : "N/A",
        user.servedBy,
        user.farm,
        user.amount.toLocaleString(),
        user.balance || 0,
        user.agingDays <= 0
          ? "Not Due"
          : user.paymentStatus === "fully_paid"
          ? "Fully Paid"
          : "Overdue",
        user.notes || "",
      ]),
    });
    doc.save("sales-invoices.pdf");
  };

  // Payment handlers
  const openPaymentModal = async (record) => {
    setSelectedTransaction(record);
    setPaymentAmount(record.balance || 0); // pre-fill with remaining balance
    setPaymentModalVisible(true);

    // Fetch customer credit balance
    if (record.stakeholderId) {
      try {
        const response = await api.get(
          `/customer-credits/${record.stakeholderId}/balance`,
        );
        if (response.data.success) {
          // Add credit balance to the selected transaction object
          setSelectedTransaction((prev) => ({
            ...prev,
            customer_credit_balance: response.data.data.total_credit || 0,
          }));
        }
      } catch (error) {
        console.error("Error fetching customer credit:", error);
        setSelectedTransaction((prev) => ({
          ...prev,
          customer_credit_balance: 0,
        }));
      }
    }
  };

  const handlePaymentSubmit = async () => {
    if (!selectedTransaction) return;

    try {
      // Validate and get form values
      const values = await form.validateFields();
      const {
        paymentMethod,
        paymentDate,
        paidBy,
        pay_reference,
        note,
        debitAccount,
        useCredit,
        creditAmount,
      } = values;

      const amountToPay = paymentAmount;

      if (amountToPay <= 0) {
        message.error("Please enter a valid payment amount greater than zero");
        return;
      }

      // Check if using credit
      const isUsingCredit = paymentMethod === "customer_credit";

      if (isUsingCredit) {
        if (!creditAmount || creditAmount <= 0) {
          message.error("Please enter a valid credit amount");
          return;
        }
        if (creditAmount > (selectedTransaction.customer_credit_balance || 0)) {
          message.error("Credit amount exceeds available balance");
          return;
        }
        if (creditAmount > (selectedTransaction.balance || 0)) {
          message.error("Credit amount cannot exceed the outstanding balance");
          return;
        }
      } else {
        if (amountToPay > (selectedTransaction.balance || 0)) {
          message.error("Payment amount cannot exceed the outstanding balance");
          return;
        }
      }

      setPaymentSubmitting(true);

      let response;

      if (isUsingCredit) {
        // Apply credit endpoint
        response = await api.post(
          `/sales/${selectedTransaction.id}/apply-credit`,
          {
            amount_to_use: creditAmount,
            customer_id: selectedTransaction.stakeholderId,
            user_id: account?.id,
            user_name: account?.firstname,
            paymentMethod: "customer_credit",
            paymentDate: paymentDate.format("YYYY-MM-DD"),
            note: note,
          },
        );
      } else {
        // Regular payment endpoint
        response = await api.post(`/record-payment`, {
          transactionId: selectedTransaction.id,
          amount: amountToPay,
          paymentDate: paymentDate.format("YYYY-MM-DD"),
          paymentMethod,
          paidBy: paidBy || account?.id,
          pay_reference,
          note,
          tableName: "sales",
          debitAccount,
          stakeholderId: selectedTransaction.stakeholderId,
          userName: account.firstname,
          userId: account.id,
        });
      }

      if (response.data.success) {
        message.success(
          response.data.message || "Payment recorded successfully",
        );
        setPaymentModalVisible(false);
        setSelectedTransaction(null);
        setPaymentAmount(0);
        // Refresh the data to update balance
        fetchReport();
      } else {
        message.error(response.data.message || "Payment failed");
      }
    } catch (error) {
      console.error("Payment error:", error);
      message.error(
        error.response?.data?.message ||
          "An error occurred while recording payment",
      );
    } finally {
      setPaymentSubmitting(false);
    }
  };

  // Confirmation modal for archiving (kept from original)
  const handleConfirmation = async () => {
    const { id, description } = confirmationData;
    axios
      .post(`/archive-sales/${id}/${description}`)
      .then(() => {
        setTransactionData((prevData) =>
          prevData.map((transaction) => {
            if (transaction.id === id) {
              return { ...transaction, archived: true };
            }
            return transaction;
          }),
        );
        message.success("Sales Transaction archived successfully.");
        setShowConfirmationModal(false);
        fetchReport(); // refresh list
      })
      .catch((error) => {
        if (error.response?.data?.error) {
          message.error(error.response.data.error);
        } else {
          console.error("Error archiving transaction:", error);
          message.error("Error archiving transaction.");
        }
        setShowConfirmationModal(false);
      });
  };

  const viewerRef = useRef(null);
  const handlePrintReceipt = () => {
    if (viewerRef.current) {
      const iframe = viewerRef.current.querySelector("iframe");
      if (iframe) iframe.contentWindow.print();
    }
  };

  const parseDate = (value) => {
    if (!value) return null;

    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  };

  const openReversalModal = (sale) => {
    setSelectedSale(sale);
    setReversalModalVisible(true);
  };

  const closeReversalModal = () => {
    setReversalModalVisible(false);
    setSelectedSale(null);
  };

  const ETIMS_STATUS_COLORS = {
    submitted: "green",
    pending: "orange",
    failed: "red",
    not_required: "default",
  };

  // Ant Design Table columns
  const columns = [
    {
      title: "Date",
      dataIndex: "recorded",
      key: "recorded",
      render: (_, record) =>
        formatFullDate(record.recorded || record.createdAt),
      sorter: (a, b) =>
        new Date(a.recorded || a.createdAt) -
        new Date(b.recorded || b.createdAt),
    },
    {
      title: "INVOICE#",
      dataIndex: "transactionId",
      key: "transactionId",
      copyable: true,
      render: (text, record) =>
        text ? (
          <Link to={`/sales/view/${record.id}`}>{text}</Link>
        ) : (
          <span>{text}</span>
        ),
    },
    {
      title: "Customer Name",
      dataIndex: "stakeholderId",
      key: "stakeholderId",
      render: (id) => (
        <Link to={`/stakeholder/details/${id}`}>
          {stake.find((s) => s.id === id)?.name || "Walk-in Customer"}
        </Link>
      ),
    },
    {
      title: "Status",
      dataIndex: "agingDays",
      key: "status",
      render: (agingDays, record) => {
        if (record.paymentStatus === "fully_paid")
          return <Tag color="green">Paid</Tag>;
        if (agingDays <= 0) return <Tag color="default">Not Due</Tag>;
        if (agingDays >= 1)
          return (
            <Tag color="red">
              OVERDUE BY {Math.abs(agingDays)} day
              {Math.abs(agingDays) > 1 ? "s" : ""}
            </Tag>
          );
        return <Tag color="blue">Unknown</Tag>;
      },
      sorter: (a, b) => a.agingDays - b.agingDays,
    },
    {
      title: "Date Due",
      dataIndex: "dueDate",
      key: "dueDate",
      render: (_, record) => {
        const date =
          parseDate(record.dueDate) ||
          parseDate(record.recorded) ||
          parseDate(record.createdAt);

        return date ? formatFullDate(date) : "-";
      },
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (amount) => (amount !== null ? formatKES(amount) : ""),
      sorter: (a, b) => a.amount - b.amount,
    },
    {
      title: "Balance Due",
      dataIndex: "balance",
      key: "balance",
      render: (balance) => (balance !== null ? formatKES(balance) : ""),
      sorter: (a, b) => (a.balance || 0) - (b.balance || 0),
    },
    {
      title: "eTIMS Status",
      dataIndex: "etims_status",
      render: (status, record) => (
        <Space>
          <Tag color={ETIMS_STATUS_COLORS[status]}>
            {status.replace("_", " ").toUpperCase()}
          </Tag>
          {record.control_unit_invoice_number && (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.control_unit_invoice_number}
            </Text>
          )}
        </Space>
      ),
    },
    {
      title: "Action",
      key: "action",
      fixed: "right",
      render: (_, record) => (
        <Space>
          <Button
            icon={<DollarOutlined />}
            disabled={record.balance === 0}
            onClick={() => openPaymentModal(record)}
            size="small"
          >
            {/* Receive Payment */}
          </Button>
          <Button
            size="small"
            icon={<RollbackOutlined />}
            danger
            onClick={() => {
              openReversalModal(record);
            }}
            disabled={record.reversed}
          >
            {/* {record.reversed_at} */}
          </Button>
        </Space>
      ),
    },
  ];

  // Table pagination config
  const paginationConfig = {
    current: page,
    pageSize: rowsPerPage,
    total: totalRows,
    showSizeChanger: true,
    pageSizeOptions: [
      "10",
      "20",
      "30",
      "40",
      "50",
      "100",
      "200",
      "500",
      "1000",
    ],
    onChange: (newPage, newPageSize) => {
      setPage(newPage);
      if (newPageSize !== rowsPerPage) {
        setRowsPerPage(newPageSize);
        setPage(1);
      }
    },
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} invoices`,
  };

  // Handle period change (string value)
  const handlePeriodChange = (value) => {
    setPage(1);
    setFilters((prev) => ({
      ...prev,
      period: value,
      startDate: "",
      endDate: "",
    }));
  };

  // Handle custom date range
  const handleDateRangeChange = (dates) => {
    if (dates) {
      setFilters((prev) => ({
        ...prev,
        startDate: dates[0].format("YYYY-MM-DD"),
        endDate: dates[1].format("YYYY-MM-DD"),
      }));
    } else {
      setFilters((prev) => ({ ...prev, startDate: "", endDate: "" }));
    }
  };

  // Prepare select options (grouped) for the form
  const paidOptions = groupAccountOptions(paidAccounts); // from your utils

  return (
    <>
      {/* Metrics using Statistic */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          {/* <Card> */}
          <Statistic
            title="Sales Invoices"
            value={metrics.totalTransactions || 0}
          />
          {/* </Card> */}
        </Col>
        <Col span={6}>
          {/* <Card> */}
          <Statistic
            title="Revenue"
            value={metrics.totalAmount || 0}
            formatter={(value) => formatKES(value)}
          />
          {/* </Card> */}
        </Col>
        <Col span={6}>
          {/* <Card> */}
          <Statistic
            title="Collected"
            value={metrics.totalPaid || 0}
            formatter={(value) => formatKES(value)}
          />
          {/* </Card> */}
        </Col>
        <Col span={6}>
          {/* <Card> */}
          <Statistic
            title="Receivables"
            value={metrics.totalOutstanding || 0}
            formatter={(value) => formatKES(value)}
          />
          {/* </Card> */}
        </Col>
      </Row>

      <Divider />
      <ProTable
        defaultSize={"small"}
        columns={columns}
        dataSource={data.filter((item) => {
          if (!searchText) return true;
          return Object.values(item).some(
            (value) =>
              typeof value === "string" &&
              value.toLowerCase().includes(searchText.toLowerCase()),
          );
        })}
        width="100%"
        loading={loading}
        pagination={paginationConfig}
        rowKey="id"
        scroll={{ x: "max-content" }}
        showSizeChanger={true}
        search={false}
        toolBarRender={() => (
          <Row gutter={[16, 16]}>
            <Col xs={24} sm={12} md={3}>
              <Select
                placeholder="Select Period"
                allowClear
                style={{ width: "100%" }}
                value={filters.period}
                onChange={handlePeriodChange}
                options={periodOptions.map((opt) => ({
                  label: opt.label,
                  value: opt.value,
                }))}
              />
            </Col>
            {filters.period === "custom" && (
              <Col xs={24} sm={12} md={3}>
                <RangePicker
                  style={{ width: "100%" }}
                  onChange={handleDateRangeChange}
                  format="YYYY-MM-DD"
                />
              </Col>
            )}
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Select Customer"
                style={{ width: "100%" }}
                allowClear
                showSearch
                optionFilterProp="label"
                value={filters.stakeholderId}
                onChange={(val) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, stakeholderId: val }));
                }}
                options={stakeholderOptions.map((opt) => ({
                  label: opt.label,
                  value: opt.value,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Select
                placeholder="Payment Status"
                style={{ width: "100%" }}
                allowClear
                value={filters.paymentStatus}
                onChange={(val) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, paymentStatus: val }));
                }}
                options={paymentOptions.map((opt) => ({
                  label: opt.label,
                  value: opt.value,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Aging Filter"
                style={{ width: "100%" }}
                allowClear
                value={filters.agingStatus}
                onChange={(val) => {
                  setPage(1);
                  setFilters((prev) => ({ ...prev, agingStatus: val }));
                }}
                options={agingOptions.map((opt) => ({
                  label: opt.label,
                  value: opt.value,
                }))}
              />
            </Col>
            <Col xs={24} sm={12} md={3}>
              <Input
                placeholder="Search..."
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Space>
                <Button icon={<FileExcelOutlined />} onClick={exportToCSV}>
                  CSV
                </Button>
                <Button icon={<FilePdfOutlined />} onClick={exportToPDF}>
                  PDF
                </Button>
              </Space>
            </Col>
          </Row>
        )}
      />

      {/* Hidden anchor for CSV download */}
      <a ref={csvLinkRef} style={{ display: "none" }} />

      {/* Confirmation Modal for Archiving */}
      <Modal
        title="Confirm Archive"
        open={showConfirmationModal}
        onOk={handleConfirmation}
        onCancel={() => setShowConfirmationModal(false)}
        okText="Archive"
        cancelText="Cancel"
      >
        <p>Are you sure you want to archive this transaction?</p>
      </Modal>

      {/* Payment Modal */}
      <Modal
        title="Record Payment"
        open={paymentModalVisible}
        onCancel={() => {
          setPaymentModalVisible(false);
          setSelectedTransaction(null);
          setPaymentAmount(0);
        }}
        width={600}
        footer={[
          <Button key="cancel" onClick={() => setPaymentModalVisible(false)}>
            Cancel
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={paymentSubmitting}
            onClick={handlePaymentSubmit}
          >
            Submit Payment
          </Button>,
        ]}
      >
        {selectedTransaction && (
          <div>
            <Descriptions
              size="small"
              bordered
              column={1}
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Invoice">
                {selectedTransaction.transactionId}
              </Descriptions.Item>
              <Descriptions.Item label="Customer">
                {stake.find((s) => s.id === selectedTransaction.stakeholderId)
                  ?.name || "Walk-in Customer"}
              </Descriptions.Item>
              <Descriptions.Item label="Total Amount">
                {formatKES(selectedTransaction.amount)}
              </Descriptions.Item>
              <Descriptions.Item label="Outstanding Balance">
                <span style={{ color: "#ff4d4f" }}>
                  {formatKES(selectedTransaction.balance)}
                </span>
              </Descriptions.Item>
              {selectedTransaction.customer_credit_balance > 0 && (
                <Descriptions.Item label="Available Credit">
                  <span style={{ color: "#52c41a" }}>
                    {formatKES(selectedTransaction.customer_credit_balance)}
                  </span>
                </Descriptions.Item>
              )}
            </Descriptions>

            <Form
              form={form}
              layout="vertical"
              initialValues={{ paymentDate: dayjs(), paymentMethod: "cash" }}
            >
              <Form.Item
                name="paymentMethod"
                label="Payment Method"
                rules={[{ required: true }]}
              >
                <Select
                  onChange={(value) => {
                    if (value === "customer_credit") {
                      form.setFieldsValue({ paymentAmount: 0 });
                    }
                  }}
                >
                  <Select.Option value="cash">Cash</Select.Option>
                  <Select.Option value="mpesa">M-Pesa</Select.Option>
                  <Select.Option value="bank_transfer">
                    Bank Transfer
                  </Select.Option>
                  {selectedTransaction.customer_credit_balance > 0 && (
                    <Select.Option value="customer_credit">
                      Customer Credit (Available:{" "}
                      {formatKES(selectedTransaction.customer_credit_balance)})
                    </Select.Option>
                  )}
                </Select>
              </Form.Item>

              <Form.Item noStyle shouldUpdate>
                {({ getFieldValue }) => {
                  const isCredit =
                    getFieldValue("paymentMethod") === "customer_credit";

                  if (isCredit) {
                    return (
                      <>
                        <Form.Item
                          name="creditAmount"
                          label="Credit Amount to Apply"
                          rules={[
                            {
                              required: true,
                              message: "Please enter credit amount",
                            },
                            {
                              validator: (_, value) => {
                                if (
                                  value >
                                  selectedTransaction.customer_credit_balance
                                ) {
                                  return Promise.reject(
                                    "Amount exceeds available credit",
                                  );
                                }
                                if (value > selectedTransaction.balance) {
                                  return Promise.reject(
                                    "Amount exceeds outstanding balance",
                                  );
                                }
                                return Promise.resolve();
                              },
                            },
                          ]}
                        >
                          <InputNumber
                            style={{ width: "100%" }}
                            min={0.01}
                            max={Math.min(
                              selectedTransaction.customer_credit_balance,
                              selectedTransaction.balance,
                            )}
                            precision={2}
                            formatter={(value) =>
                              `KES ${value}`.replace(
                                /\B(?=(\d{3})+(?!\d))/g,
                                ",",
                              )
                            }
                            parser={(value) =>
                              value.replace(/KES\s?|(,*)/g, "")
                            }
                          />
                        </Form.Item>
                        <Alert
                          message="Using Customer Credit"
                          description="This will deduct from the customer's prepaid balance and record as a payment."
                          type="info"
                          showIcon
                          style={{ marginBottom: 16 }}
                        />
                      </>
                    );
                  }

                  return (
                    <>
                      <Form.Item
                        name="paymentAmount"
                        label="Payment Amount"
                        rules={[
                          {
                            required: true,
                            message: "Please enter payment amount",
                          },
                        ]}
                      >
                        <InputNumber
                          style={{ width: "100%" }}
                          min={0.01}
                          max={selectedTransaction.balance}
                          precision={2}
                          value={paymentAmount}
                          onChange={(value) => setPaymentAmount(value || 0)}
                          formatter={(value) =>
                            `KES ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                          }
                          parser={(value) => value.replace(/KES\s?|(,*)/g, "")}
                        />
                      </Form.Item>

                      <Row gutter={16}>
                        <Col span={12}>
                          <Form.Item
                            name="debitAccount"
                            label="Debit Account"
                            rules={[
                              {
                                required: true,
                                message: "Please select debit account",
                              },
                            ]}
                          >
                            <Select
                              options={paidOptions}
                              placeholder="Select debit account"
                              showSearch
                              optionFilterProp="label"
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item name="pay_reference" label="Reference">
                            <Input />
                          </Form.Item>
                        </Col>
                      </Row>
                    </>
                  );
                }}
              </Form.Item>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    name="paymentDate"
                    label="Payment Date"
                    rules={[{ required: true }]}
                  >
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    name="paidBy"
                    label="Paid By"
                    rules={[{ required: true }]}
                  >
                    <Select showSearch optionFilterProp="children">
                      {users.map((user) => (
                        <Select.Option key={user.id} value={user.id}>
                          {user.firstname} {user.lastname}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item name="note" label="Note">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Form>
          </div>
        )}
      </Modal>

      {/* Receipt Modal */}
      {showReceiptModal && (
        <Modal
          title="Receipt"
          open={showReceiptModal}
          onCancel={handleCloseReceiptModal}
          width="80%"
          footer={[
            receiptData && receiptData.transactionId && (
              <PDFDownloadLink
                key="download"
                document={
                  <PDFContent
                    id={receiptData?.id}
                    transactionData={receiptData}
                    profile={profile}
                    stake={stake}
                  />
                }
                fileName={`${receiptData?.transactionId}_${receiptData?.createdAt}.pdf`}
              >
                {({ loading }) =>
                  loading ? (
                    <Button loading>Loading...</Button>
                  ) : (
                    <Button icon={<DownloadOutlined />}>Download</Button>
                  )
                }
              </PDFDownloadLink>
            ),
            <Button
              key="print"
              icon={<PrinterOutlined />}
              onClick={handlePrintReceipt}
            >
              Print Receipt
            </Button>,
            <Button key="close" onClick={handleCloseReceiptModal}>
              Close
            </Button>,
          ]}
        >
          <Receipt transactionId={transactionId} />
          <div ref={viewerRef} style={{ display: "none" }}>
            <PDFViewer width="226.77" height="400">
              <PDFContent
                id={receiptData?.id}
                transactionData={receiptData}
                profile={profile}
                stake={stake}
                apiUrl={apiUrl}
              />
            </PDFViewer>
          </div>
        </Modal>
      )}

      {/* Receipt Modal */}
      {showReceipt && (
        <Modal
          title="Receipt"
          open={showReceiptModal}
          onCancel={handleCloseReceiptModal}
          width="80%"
          footer={[
            receiptData && receiptData.transactionId && (
              <PDFDownloadLink
                key="download"
                document={
                  <PDFContent
                    id={receiptData?.id}
                    transactionData={receiptData}
                    profile={profile}
                    stake={stake}
                  />
                }
                fileName={`${receiptData?.transactionId}_${receiptData?.createdAt}.pdf`}
              >
                {({ loading }) =>
                  loading ? (
                    <Button loading>Loading...</Button>
                  ) : (
                    <Button icon={<DownloadOutlined />}>Download</Button>
                  )
                }
              </PDFDownloadLink>
            ),
            <Button
              key="print"
              icon={<PrinterOutlined />}
              onClick={handlePrintReceipt}
            >
              Print Receipt
            </Button>,
            <Button key="close" onClick={handleCloseReceiptModal}>
              Close
            </Button>,
          ]}
        >
          <Receipt transactionId={transactionId} />
          {/* Hidden PDF viewer for printing */}
          <div ref={viewerRef} style={{ display: "none" }}>
            <PDFViewer width="226.77" height="400">
              <PDFContent
                id={receiptData?.id}
                transactionData={receiptData}
                profile={profile}
                stake={stake}
                apiUrl={apiUrl}
              />
            </PDFViewer>
          </div>
        </Modal>
      )}

      {/* Reversal Modal */}
      {reversalModalVisible && (
        <SaleReversalModal
          visible={reversalModalVisible}
          saleId={selectedSale?.id}
          onClose={closeReversalModal}
          onSuccess={fetchReport}
        />
      )}
    </>
  );
};

export default SalesInvoices;
